import type { Auth, User } from 'firebase/auth'
import type { Functions } from 'firebase/functions'
import type { SessionUser } from '@domain/auth'
import type {
  AuthGateway,
  BootstrapRefusal,
  BootstrapResult,
  SignInRefusal,
  SignInResult,
} from '@domain/ports/auth-gateway'
import { bootstrapHousehold, observeUser, signInWithGoogle, signOutUser } from './auth'

/**
 * A implementação da porta `AuthGateway` sobre o Firebase.
 *
 * Este arquivo é a **única** tradução entre o vocabulário do SDK (`User`,
 * `FirebaseError`, códigos com prefixo) e o vocabulário do app (`SessionUser`,
 * motivos de recusa). Nada acima dele conhece o Firebase — a fronteira é
 * lintada e falha o build.
 */

/** `User` do SDK → o tipo que atravessa a fronteira. */
export function toSessionUser(user: User): SessionUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  }
}

function errorCode(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return typeof error.code === 'string' ? error.code : null
  }
  return null
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : ''
}

/** Os códigos em que o SDK avisa que **a pessoa** desistiu do popup. */
const CANCELLED_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]

/**
 * Por que a entrada foi recusada.
 *
 * **A recusa da blocking function NÃO chega como `permission-denied`.** Isso
 * foi medido, não lido: com o emulador de Auth + Functions rodando as functions
 * deste repositório, um login com e-mail fora da allowlist chega ao cliente
 * como
 *
 * ```
 * code:    'auth/internal-error'
 * message: 'Firebase: ((HTTP request to …/blockUnknownUsers returned HTTP
 *           error 403: {"error":{"message":"Esta conta não tem acesso ao
 *           Porquinho.","status":"PERMISSION_DENIED"}})) (auth/internal-error).'
 * ```
 *
 * O plano da Task 8 dizia "se o login falhar por `permission-denied`"; um
 * `error.code === 'permission-denied'` aqui **nunca dispararia**, e a pessoa
 * barrada veria "erro ao entrar" — exatamente o que o Passo 2 proíbe. Daí a
 * checagem ser código + marcador na mensagem.
 *
 * A dupla condição é de propósito, e o viés é para o lado seguro: `PERMISSION_DENIED`
 * sozinho seria casamento por substring solta, e `auth/internal-error` sozinho
 * marcaria como "sem acesso" qualquer falha interna, inclusive rede. Se um dia
 * o SDK mudar o texto, esta função degrada para `failed` ("tente de novo") —
 * mensagem pior, nunca mentira: ninguém é acusado de não ter acesso por causa
 * de uma queda de rede.
 */
export function classifySignIn(error: unknown): SignInRefusal {
  const code = errorCode(error)
  if (code !== null && CANCELLED_CODES.includes(code)) return 'cancelled'
  if (code === 'auth/internal-error' && errorMessage(error).includes('PERMISSION_DENIED')) {
    return 'not-allowed'
  }
  return 'failed'
}

/**
 * Por que o bootstrap falhou — e, com isso, se retentar tem sentido.
 *
 * O código do lado do cliente vem **com o prefixo `functions/`**: o servidor
 * lança `HttpsError('already-exists', …)` (decisão 57) e o SDK entrega
 * `code: 'functions/already-exists'`. Também medido contra o emulador, com um
 * membro semeado em outro uid para o mesmo e-mail. Comparar com
 * `'already-exists'` puro — como o repositório de household compara o
 * `permission-denied` do Firestore, que não tem prefixo — daria sempre falso, e
 * a UI ofereceria "tentar de novo" para a única falha que não se conserta.
 */
export function classifyBootstrap(error: unknown): BootstrapRefusal {
  return errorCode(error) === 'functions/already-exists' ? 'already-linked' : 'retryable'
}

export function createFirebaseAuthGateway(services: {
  auth: Auth
  functions: Functions
}): AuthGateway {
  const { auth, functions } = services

  return {
    observeUser: (onChange) =>
      observeUser(auth, (user) => {
        onChange(user === null ? null : toSessionUser(user))
      }),

    signIn: async (): Promise<SignInResult> => {
      try {
        await signInWithGoogle(auth)
        return { ok: true }
      } catch (error) {
        const refusal = classifySignIn(error)
        // Fica no console mesmo quando a tela mostra a mensagem genérica: o
        // motivo classificado como `failed` é justamente aquele cuja causa a
        // tela não sabe dizer.
        if (refusal === 'failed') console.error('signIn falhou', error)
        return { ok: false, refusal }
      }
    },

    signOut: () => signOutUser(auth),

    bootstrapHousehold: async (): Promise<BootstrapResult> => {
      try {
        await bootstrapHousehold(functions)
        return { ok: true }
      } catch (error) {
        console.error('bootstrapHousehold falhou', error)
        return { ok: false, refusal: classifyBootstrap(error) }
      }
    },
  }
}
